import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Redirect,
} from 'react-router-dom';
import { connect, Provider } from 'react-redux';
import { LastLocationProvider } from 'react-router-last-location';
import Rollbar from 'rollbar';

import store from './redux/store';

import { PublicRoute, AdminRoute } from './components/routes';
import { setupHttpConfig } from './utils/http';
import {
  Login,
  ForgotPassword,
  Welcome,
  OnBoardingInfo,
  Complete,
  Survey,
  Profile,
  Solutions,
  Resources,
  Invite,
  ManagerInvite,
  ManagerDashboard,
  Verify,
  Feedback,
  Team,
  UserDetails,
  CompanyCode,
  ProfileSetup,
  PasswordSetup,
  Completion,
} from './pages';
import { ToasterProvider } from './components/Toaster/useToaster';
import { Toaster, Loader } from './components';
import { getUserInfo } from './redux/actions/AuthenticationAction';
import Smartlook from 'smartlook-client';

const envs = ['production', 'staging'];
function App({ userInfo: { loading, error, info }, getUser }) {
  useEffect(() => {
    setupHttpConfig();
    if (loading) {
      getUser();
    }
    if (envs.includes(process.env.REACT_APP_ENVIRONMENT)) {
      new Rollbar({
        accessToken: process.env.REACT_APP_ROLLBAR_API,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          environment: process.env.REACT_APP_ENVIRONMENT,
        },
      });
    }

    Smartlook.init('12f4576faeb686d7c174a61c1f5ed14bcc1ab521', {
      region: 'eu',
      cookies: true,
    });

    // smartlook('record', { forms: true, numbers: true, emails: true, ips: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loader />;

  const {
    company: { google_data_studio_url } = {},
    google_data_studio_url: employee_data,
  } = info || {};
  const studio_url = employee_data || google_data_studio_url;
  return (
    <div className="app-container">
      <ToasterProvider>
        <Toaster />
        <Provider store={store}>
          <Router>
            <LastLocationProvider>
              <Switch>
                <PublicRoute path="/login" exact component={Login} />
                <PublicRoute
                  path="/forgot-password"
                  exact
                  component={ForgotPassword}
                />
                <PublicRoute path="/welcome" exact component={Welcome} />
                <PublicRoute path="/info" exact component={OnBoardingInfo} />
                <PublicRoute path="/complete" exact component={Complete} />
                <AdminRoute
                  path="/"
                  exact
                  component={Survey}
                  roles={['manager', 'employee', 'clevel']}
                />
                <AdminRoute
                  path="/dashboard"
                  exact
                  component={Survey}
                  roles={['manager', 'employee', 'clevel']}
                />
                {studio_url && (
                  <AdminRoute
                    path="/reports"
                    exact
                    component={ManagerDashboard}
                    roles={['manager', 'employee', 'clevel']}
                  />
                )}
                <AdminRoute
                  path="/profile"
                  exact
                  component={Profile}
                  roles={['manager', 'employee', 'clevel']}
                />
                <AdminRoute
                  path="/action-steps"
                  exact
                  component={Solutions}
                  roles={['manager', 'employee', 'clevel']}
                />
                <AdminRoute
                  path="/resources"
                  exact
                  component={Resources}
                  roles={['manager', 'employee', 'clevel']}
                />
                <AdminRoute
                  path="/manager-invite"
                  exact
                  component={ManagerInvite}
                  roles={['manager', 'clevel']}
                />
                <AdminRoute
                  path="/invite"
                  noSidebar
                  exact
                  component={Invite}
                  roles={['manager', 'clevel']}
                />
                <AdminRoute
                  path="/manager-dashboard"
                  exact
                  component={ManagerDashboard}
                  roles={['manager', 'clevel']}
                />
                <AdminRoute
                  path="/feedback"
                  exact
                  component={Feedback}
                  roles={['manager', 'employee', 'clevel']}
                />
                <AdminRoute
                  path="/team"
                  exact
                  component={Team}
                  roles={['manager', 'clevel']}
                />
                <AdminRoute
                  path="/team/:id"
                  exact
                  component={UserDetails}
                  roles={['manager', 'clevel']}
                />
                <PublicRoute path="/verify" exact component={Verify} />
                <PublicRoute path="/onboarding/company-code" exact component={CompanyCode} />
                <PublicRoute path="/onboarding/profile-setup" exact component={ProfileSetup} />
                <PublicRoute path="/onboarding/password-setup" exact component={PasswordSetup} />
                <PublicRoute path="/onboarding/completion" exact component={Completion} />
                <Redirect from="*" to="/" />
              </Switch>
            </LastLocationProvider>
          </Router>
        </Provider>
      </ToasterProvider>
    </div>
  );
}

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
});

const mapDispatchToProps = (dispatch) => ({
  getUser: () => {
    dispatch(getUserInfo());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
